import { io, Socket } from 'socket.io-client';

// Interface matching your existing UI components
export interface Notification {
    id: string;
    type: 'project_drawing' | 'comment' | 'drawing_sent' | 'upload_failed' | 'general';
    title: string;
    description: string;
    timestamp: string;
    isRead: boolean;
    clientName?: string;
    estimateId?: string;
    // Additional fields from backend
    body?: string;
    data?: Record<string, any>;
    createdAt?: string;
}

export interface NotificationServiceConfig {
    serverUrl: string;
    token: string;
    onNotification?: (notification: Notification) => void;
    onUnreadCount?: (count: number) => void;
    onError?: (error: any) => void;
    onConnectionChange?: (connected: boolean) => void;
    onSystemAnnouncement?: (payload: Record<string, unknown>) => void;
}

export class NotificationService {
    private socket: Socket | null = null;
    private config: NotificationServiceConfig;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private reconnectInterval: number | null = null;

    constructor(config: NotificationServiceConfig) {
        this.config = config;
    }

    connect(): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                this.socket = io(`${this.config.serverUrl}/notifications`, {
                    auth: {
                        token: this.config.token,
                    },
                    transports: ['websocket', 'polling'],
                    timeout: 10000,
                });

                this.socket.on('connect', () => {
                    console.log('[NotificationService] Connected to notifications WebSocket');
                    this.reconnectAttempts = 0;
                    this.config.onConnectionChange?.(true);
                    resolve();
                });

                this.socket.on('connected', (data: any) => {
                    console.log('[NotificationService] Notification service connected:', data);
                });

                this.socket.on('new_notification', (notification: any) => {
                    console.log('[NotificationService] New notification received:', notification);
                    const mappedNotification = this.mapBackendNotification(notification);
                    this.config.onNotification?.(mappedNotification);
                });

                this.socket.on('system_announcement', (payload: Record<string, unknown>) => {
                    import('@/hooks/useSystemStatus').then(({ dispatchSystemStatusUpdate }) => {
                        dispatchSystemStatusUpdate(payload as unknown as import('@/hooks/useSystemStatus').SystemStatus);
                    });
                    this.config.onSystemAnnouncement?.(payload);
                });

                this.socket.on('unread_count', (data: { count: number }) => {
                    console.log('[NotificationService] Unread count updated:', data.count);
                    this.config.onUnreadCount?.(data.count);
                });

                this.socket.on('notification_marked_read', (data: any) => {
                    console.log('[NotificationService] Notification marked as read:', data);
                });

                this.socket.on('all_notifications_marked_read', (data: any) => {
                    console.log('[NotificationService] All notifications marked as read:', data);
                    this.config.onUnreadCount?.(0);
                });

                this.socket.on('error', (error: any) => {
                    console.error('[NotificationService] WebSocket error:', error);
                    this.config.onError?.(error);
                });

                this.socket.on('disconnect', (reason: any) => {
                    console.log('[NotificationService] Disconnected from notifications:', reason);
                    this.config.onConnectionChange?.(false);
                    this.handleReconnect();
                });

                this.socket.on('connect_error', (error: any) => {
                    console.error('[NotificationService] Connection error:', error);
                    this.config.onConnectionChange?.(false);
                    this.config.onError?.(error);
                    reject(error);
                });

            } catch (error) {
                console.error('[NotificationService] Failed to create socket connection:', error);
                reject(error);
            }
        });
    }

    disconnect(): void {
        if (this.reconnectInterval) {
            clearTimeout(this.reconnectInterval);
            this.reconnectInterval = null;
        }

        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
        this.config.onConnectionChange?.(false);
    }

    markAsRead(notificationId: string): void {
        if (this.socket?.connected) {
            this.socket.emit('mark_notification_read', { notificationId });
        } else {
            console.warn('[NotificationService] Cannot mark as read - not connected');
        }
    }

    markAllAsRead(): void {
        if (this.socket?.connected) {
            this.socket.emit('mark_all_notifications_read');
        } else {
            console.warn('[NotificationService] Cannot mark all as read - not connected');
        }
    }

    getUnreadCount(): void {
        if (this.socket?.connected) {
            this.socket.emit('get_unread_count');
        } else {
            console.warn('[NotificationService] Cannot get unread count - not connected');
        }
    }

    isConnected(): boolean {
        return this.socket?.connected || false;
    }

    private handleReconnect(): void {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            const delay = Math.pow(2, this.reconnectAttempts) * 1000; // Exponential backoff

            this.reconnectInterval = window.setTimeout(() => {
                console.log(
                    `[NotificationService] Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`
                );
                this.connect().catch((error) => {
                    console.error('[NotificationService] Reconnection failed:', error);
                });
            }, delay);
        } else {
            console.error('[NotificationService] Max reconnection attempts reached');
            this.config.onError?.(new Error('Maximum reconnection attempts reached'));
        }
    }

    // Map backend notification format to your UI format
    private mapBackendNotification(backendNotification: any): Notification {
        // Map backend notification type to your UI types
        const mapNotificationType = (backendType?: string): Notification['type'] => {
            if (!backendType) return 'general';

            const typeMap: Record<string, Notification['type']> = {
                'project_drawing': 'project_drawing',
                'comment': 'comment',
                'drawing_sent': 'drawing_sent',
                'upload_failed': 'upload_failed',
                'project_update': 'general',
                'system': 'general',
                'chat': 'general',
                'message': 'general',
                'new_message': 'general',
                'chat_message': 'general'
            };

            return typeMap[backendType.toLowerCase()] || 'general';
        };

        // Extract client name and estimate ID from notification data
        const extractClientInfo = (data?: Record<string, any>) => {
            return {
                clientName: data?.clientName || data?.client?.name || undefined,
                estimateId: data?.estimateId || data?.projectId || undefined
            };
        };

        const { clientName, estimateId } = extractClientInfo(backendNotification.data);

        return {
            id: backendNotification.id,
            type: mapNotificationType(backendNotification.type),
            title: backendNotification.title,
            description: backendNotification.body || backendNotification.description || '',
            timestamp: backendNotification.createdAt
                ? new Date(backendNotification.createdAt).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                })
                : new Date().toLocaleString(),
            isRead: backendNotification.read || false,
            clientName,
            estimateId,
            // Keep original data for reference
            data: backendNotification.data
        };
    }
}